import { selectCalendarTimeZone } from '@src/packages/utils/dates/calendar';
import React from 'react';

import { Empty } from 'antd';
import { useSelector } from 'react-redux';


import { ResponsiveTable } from '@src/components/Table';
import UserListDrawer from '@src/components/Users/UserListDrawer';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import FilterActiveTags from '@src/components/FilterActiveTags/FilterActiveTags';
import FilterContainer from '@src/components/FilterContainer';
import { FilterValues } from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import ListViewportLayout from '@src/components/ListViewportLayout';
import SecondaryPanel from '@src/components/SecondaryPanel/SecondaryPanel';
import ReportDivisionAccessGate from '@src/packages/dashboard/components/ReportDivisionAccessGate';
import { useServerReportPdf } from '@src/packages/dashboard/hooks/useServerReportPdf';
import IntlMessages from '@src/util/IntlMessages';
import ReportTabsBar from '../../components/ReportTabsBar';

import ApplyReportFiltersButton from '@src/packages/dashboard/components/ApplyReportFiltersButton';
import ReportEventDrawer from '@src/packages/dashboard/components/ReportEventDrawer';
import type { ChartView } from '@src/packages/dashboard/components/ReportSummary/types';
import EventTypesDrawer from '@src/packages/events/components/EventTypesDrawer';

import { getDateTypeIndexUTC } from '../../utils';
import ScheduleDashboardFilters from './components/ScheduleDashboardFilters';
import ScheduleSummary from './components/ScheduleSummary';
import { useScheduleHistorySections } from './components/ScheduleSummary/hooks/useScheduleHistorySections';
import { buildScheduleHistoryQuery } from './components/ScheduleSummary/scheduleHistoryQuery';
import { useSchedulesDashboard } from './hooks/useSchedulesDashboard';
import styles from './styles.module.less';
import type { SchedulesDashboardReduxState } from './types';

const EMPTY_DIVISION_IDS: string[] = [];

export const SchedulesDashboardContent: React.FC = () => {
  const [activeTab, setActiveTab] = React.useState('summary');
  const viewportBodyRef = React.useRef<HTMLDivElement>(null);
  const filterWheelRegionRef = React.useRef<HTMLDivElement>(null);
  const tabsWheelRegionRef = React.useRef<HTMLDivElement>(null);
  const [complianceChartView, setComplianceChartView] = React.useState<ChartView>('bar');
  const organizationTimeZone = useSelector(selectCalendarTimeZone);

  const organizationId = useSelector(
    (state: SchedulesDashboardReduxState) => state.organizations?.organization?.id ?? '',
  );
  const {
    showEventTypeDrawer,
    groupBy,
    openTimelineEvents,
    intl,
    setShowEventTypeDrawer,
    onBackFromTimeline,
    tableDataByDay,
    drawerState,
    toggleDrawer,
    selectedUsers,
    columns,
    filters,
    setFilters,
    initialFilters,
    chartFilters,
    draftChartFilters,
    hasPendingFilters,
    applyFilters,
    fetch,
    mainLoading,
    queryDivisionIds,
    canQueryDivisions,
    permissionsLoaded,
    disabledDate,
    setSelectedSchedules,
    setScheduleSelectorLoading,
    toggle,
    isOpen,
  } = useSchedulesDashboard();

  const reportDivisionIds = queryDivisionIds ?? EMPTY_DIVISION_IDS;
  const scheduleHistoryQuery = React.useMemo(
    () =>
      organizationId && canQueryDivisions
        ? buildScheduleHistoryQuery(
            chartFilters,
            organizationId,
            reportDivisionIds,
            organizationTimeZone,
          )
        : undefined,
    [canQueryDivisions, chartFilters, organizationId, organizationTimeZone, reportDivisionIds],
  );
  const scheduleHistory = useScheduleHistorySections(scheduleHistoryQuery);
  const initialFiltersRef = React.useRef(initialFilters);

  const handleStickyControlsWheel = React.useCallback((event: WheelEvent) => {
    if (
      event.ctrlKey ||
      event.shiftKey ||
      event.deltaY === 0 ||
      Math.abs(event.deltaX) > Math.abs(event.deltaY) ||
      !viewportBodyRef.current
    ) {
      return;
    }

    event.preventDefault();
    viewportBodyRef.current.scrollTop += event.deltaY;
  }, []);

  React.useEffect(() => {
    const regions = [filterWheelRegionRef.current, tabsWheelRegionRef.current].filter(
      (region): region is HTMLDivElement => Boolean(region),
    );
    const options: AddEventListenerOptions = { passive: false };

    regions.forEach(region => {
      region.addEventListener('wheel', handleStickyControlsWheel, options);
    });

    return () => {
      regions.forEach(region => {
        region.removeEventListener('wheel', handleStickyControlsWheel, options);
      });
    };
  }, [handleStickyControlsWheel]);

  const filterPanel = React.useMemo(
    () => (
      <ScheduleDashboardFilters
        handleFilters={setFilters}
        filters={filters}
        chartFilters={draftChartFilters}
        divisionIds={queryDivisionIds}
        disabledDate={disabledDate}
        onSchedulesChange={setSelectedSchedules}
        onScheduleSelectorLoading={setScheduleSelectorLoading}
      />
    ),
    [
      draftChartFilters,
      disabledDate,
      filters,
      queryDivisionIds,
      setFilters,
      setScheduleSelectorLoading,
      setSelectedSchedules,
    ],
  );

  const serverPdfAction = useServerReportPdf(
    scheduleHistoryQuery ? { ...scheduleHistoryQuery, reportType: 'schedules', variant: 'full' } : undefined,
  );

  return (
    <>
      <SecondaryPanel.Panel
        title={intl.formatMessage({ id: 'dashboards.chart.filters' })}
        initialValues={initialFiltersRef.current as FilterValues}
      >
        {filterPanel}
        <ApplyReportFiltersButton
          onClick={applyFilters}
          disabled={!hasPendingFilters || mainLoading}
          loading={mainLoading}
        />
      </SecondaryPanel.Panel>
      <BoxContainer className={styles.reportActions} fullContent fixed bottomBorder>
        <FilterContainer
          className="dashboard-report-header"
          title={<Title.Header value={<IntlMessages id="dashboards.schedules.title" />} />}
          actionButtons={[
            {
              label: intl.formatMessage({
                id: isOpen ? 'filterDropdown.closeFilters' : 'dashboard.tooltip.filter',
              }),
              iconName: 'filter_alt',
              type: 'secondary',
              action: toggle,
              className: isOpen ? 'gx-border-primary' : '',
            },
            {
              label: intl.formatMessage({ id: 'backoffice.button.refresh' }),
              iconName: 'refresh',
              type: 'secondary',
              action: activeTab === 'summary' ? scheduleHistory.refresh : fetch,
              disabled: !canQueryDivisions || (activeTab === 'summary' ? scheduleHistory.loading : mainLoading),
            },
            serverPdfAction,
          ]}
        />
      </BoxContainer>
      <FilterActiveTags.Container ref={filterWheelRegionRef} className={styles.reportFilters}>
        <FilterActiveTags.Bar>
          <FilterActiveTags.ScrollArea>
            <FilterActiveTags onAddFilter={toggle} onCustomFilterChange={setFilters} />
          </FilterActiveTags.ScrollArea>
        </FilterActiveTags.Bar>
      </FilterActiveTags.Container>
      <BoxContainer loading={activeTab === 'detail' && mainLoading} fullContent>
        <SecondaryPanel.Content>
          <ListViewportLayout
            bodyRef={viewportBodyRef}
            className={styles.reportViewport}
            bodyClassName={styles.reportBody}
            viewportOffset={160}
            minHeight="calc(100dvh - 160px)"
            sidePanelOpen={isOpen}
            constrainToScrollParent={false}
            top={
              <ReportTabsBar
                containerRef={tabsWheelRegionRef}
                className={styles.tabsWheelRegion}
                bleed
                activeKey={activeTab}
                onChange={setActiveTab}
                items={[
                  { key: 'summary', label: <IntlMessages id="dashboards.schedules.tab.summary" /> },
                  { key: 'detail', label: <IntlMessages id="dashboards.schedules.tab.detail" /> },
                ]}
              />
            }
            topClassName={styles.tabsHeader}
          >
            <ReportDivisionAccessGate
              canQuery={canQueryDivisions}
              permissionsLoaded={permissionsLoaded}
            >
              {activeTab === 'summary' ? (
                <ScheduleSummary
                  filters={chartFilters}
                  divisionIds={queryDivisionIds}
                  historyQuery={scheduleHistoryQuery}
                  history={scheduleHistory}
                  complianceChartView={complianceChartView}
                  onComplianceChartViewChange={setComplianceChartView}
                />
              ) : (
                <>
                  <div className={styles.progressItemsList}>
                    {!mainLoading && tableDataByDay.length === 0 && (
                      <Empty description={<IntlMessages id="components.empty.description" />} />
                    )}
                    {tableDataByDay.map(dayData => (
                      <div key={dayData.day} className={styles.dayContainer}>
                        <h2 className={styles.day}>
                          {getDateTypeIndexUTC(dayData.day, groupBy, intl)}
                        </h2>
                        <ResponsiveTable
                          className={styles.table}
                          dataSource={dayData.dataSource}
                          columns={columns}
                          loading={false}
                        />
                      </div>
                    ))}
                  </div>
                </>
              )}
            </ReportDivisionAccessGate>
          </ListViewportLayout>
        </SecondaryPanel.Content>
      </BoxContainer>
      {openTimelineEvents && (
        <ReportEventDrawer
          isVisible={openTimelineEvents}
          onCancel={onBackFromTimeline}
          organizationId={organizationId}
          divisionIds={reportDivisionIds}
          scheduleType="schedule"
        />
      )}
      {showEventTypeDrawer && (
        <EventTypesDrawer
          isVisible={showEventTypeDrawer}
          onCancel={() => setShowEventTypeDrawer(false)}
        />
      )}
      <UserListDrawer
        isVisible={drawerState.users}
        onCancel={() => toggleDrawer('users')}
        users={selectedUsers?.users || []}
        groups={selectedUsers?.groups || []}
      />
    </>
  );
};

const SchedulesDashboard: React.FC = () => <SchedulesDashboardContent />;

export default SchedulesDashboard;
